import { Categories } from './categories';
import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import { pushToMtm } from './mtm';

interface Account {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
}

const ENV = process.env['REACT_APP_ENV'] || process.env['NX_APP_ENV'];
const DEV = !ENV || ENV === 'dev';

export type AnalyticsExtra = {
  [key: string]: number | string | undefined | boolean;
};
export type AnalyticsTags = { [key: string]: string };
export type Event = {
  category: string;
  action: string;
  label?: string;
  nonInteraction?: boolean;
  [key: string]: any;
};

export class Analytics {
  lastGroup?: string;
  annonymousId?: string;

  set(obj: any): void {
    pushToMtm(obj);
  }

  track<D extends {}>(event: string, data?: D) {
    this.set({ ...data, event });
  }

  group(organizationId?: string, organizationData?: any) {
    if (!organizationId) {
      return this.lastGroup;
    }
    this.lastGroup = organizationId;
    this.track('group', { organizationId, ...organizationData });
    Sentry.setTag('organization.id', organizationId);
    Sentry.setTag('organization.name', organizationData.name);
    return this.lastGroup;
  }

  event(event: string | Event, data?: any): void {
    const baseEvent = typeof event === 'string' ? { action: event } : event;

    const finalEvent = {
      category: Categories.GENERAL,
      label: '',
      nonInteraction: false,
      ...baseEvent,
      data,
    };
    this.track('Action', finalEvent);
    Sentry.addBreadcrumb({
      type: 'user',
      category: finalEvent.category,
      level: 'info',
      message: [finalEvent.action, finalEvent.label]
        .filter(Boolean)
        .join(' - '),
      data,
    });
  }

  identify(accountInfo: any, id?: string) {
    if (!accountInfo?.roles) {
      return;
    }

    Sentry.setUser(accountInfo);
    this.track('Identify', {
      accountId: id,
      accountInfo,
      role: accountInfo.roles.includes('ADMIN')
        ? 'admin'
        : accountInfo.roles.includes('CLIENT')
        ? 'client'
        : 'participant',
      participantId: accountInfo.linkedParticipants?.[0],
    });
  }

  identifyAnonymous() {
    if (!this.annonymousId) {
      this.annonymousId = uuidv4();
    }
    Sentry.setUser({
      id: this.annonymousId,
      roles: ['ANONYMOUS'],
    });
  }

  pageChanged(_location: { pathname: string; search: string }) {}

  static warnError(
    error: unknown,
    context?: AnalyticsExtra,
    tags?: AnalyticsTags,
  ) {
    if (!(error instanceof Error)) {
      return;
    }
    pushToMtm({
      action: 'warning',
      category: 'ERROR',
      description: error.message,
      fatal: false,
      tags,
      context,
    });
    if (context) {
      Sentry.setExtras(context);
    }
    if (tags) {
      Sentry.setTags(tags);
    }
    Sentry.captureException(error, { level: 'warning' });
  }

  static error(error: unknown, context?: AnalyticsExtra, tags?: AnalyticsTags) {
    if (!(error instanceof Error)) {
      return;
    }
    return this.fatal(error, context, tags);
  }

  static fatal(error: unknown, context?: AnalyticsExtra, tags?: AnalyticsTags) {
    if (!(error instanceof Error)) {
      return;
    }
    pushToMtm({
      event: 'error',
      category: 'ERROR',
      description: error.message,
      fatal: true,
      tags,
      context,
    });

    if (context) {
      Sentry.setExtras(context);
    }
    if (tags) {
      Sentry.setTags(tags);
    }
    Sentry.captureException(error);
  }

  static apiFatal(error: Error, context?: AnalyticsExtra) {
    return this.fatal(error, context, { type: 'api' });
  }

  static assert(condition: boolean, name: string, context?: Object): boolean {
    if (condition) {
      return condition;
    }
    const error = this.assertionFailed(name, context);
    if (DEV) {
      throw error;
    }
    return false;
  }

  static assertionFailed(name: string, context?: Object): Error {
    const message = `Assertion ${name} failed`;
    const error = new Error(message);
    Sentry.captureMessage(name, 'warning');
    return error;
  }

  login(account: Account) {
    this.event({
      action: 'login',
      category: Categories.GENERAL,
      user: account,
      role: account.roles.includes('ADMIN') ? 'ADMIN' : 'CLIENT',
    });
  }

  signup(account: Account) {
    this.event({
      action: 'sign_up',
      category: Categories.SIGNUP,
      id: account.id,
      role: account.roles.includes('ADMIN') ? 'ADMIN' : 'CLIENT',
      user: account,
    });
  }

  logout() {
    this.event({
      action: 'Signed Out',
      category: Categories.GENERAL,
    });

    this.track('reset');
    Sentry.setUser(null);
    window._mtm = [];
  }

  participantSubscribe(participant: any) {
    this.event({
      category: Categories.SURVEY,
      action: 'particiant_subscribe',
      label: 'Confirm booking',
      participant,
    });
  }

  trackEstimationRefresh(value: number) {
    this.event(
      {
        category: Categories.RECRUIT,
        action: 'estimation_refresh',
      },
      { value: value / 100, centsValue: value },
    );
  }

  trackSaveQuotation(value: number) {
    this.event(
      {
        category: Categories.RECRUIT,
        action: 'save_quotation',
      },
      { value: value / 100, centsValue: value },
    );
  }

  trackAcceptQuotation(value: number) {
    this.event(
      {
        category: Categories.RECRUIT,
        action: 'accept_quotation',
      },
      { value: value / 100, centsValue: value },
    );
  }
}
